export type InputState = {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  fire: boolean;
  firePressed: boolean;
  pause?: boolean;
  restartPressed?: boolean;
};

export type GameState = 'start' | 'playing' | 'gameover';

export type EnemyType = 'scout' | 'chaser' | 'heavy';

export type Enemy = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  type: EnemyType;
  state: 'normal' | 'trapped';
  trappedTimer: number; // seconds remaining in trapped bubble
  maxTrappedTime: number;
};

export type Player = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number; // in radians
  radius: number;
  lives: number;
  invulnTimer: number;
  fireCooldown: number;
};

export type Bullet = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  life: number;
};

export type PopEvent = {
  x: number;
  y: number;
  comboCount: number;
  scoreGained: number;
};

export type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  life: number;
  maxLife: number;
  size: number;
};

export class AstroBubbleEngine {
  width: number;
  height: number;

  state: GameState = 'start';
  player: Player;
  enemies: Enemy[] = [];
  bullets: Bullet[] = [];
  particles: Particle[] = [];
  popEvents: PopEvent[] = [];

  score: number = 0;
  highScore: number = 0;
  wave: number = 1;
  waveTransitionTimer: number = 0;

  private nextId: number = 1;

  // Configuration constants
  readonly PLAYER_ACCEL = 450; // px/s^2
  readonly PLAYER_FRICTION = 0.98; // velocity damping per frame @ 60fps
  readonly PLAYER_MAX_SPEED = 350;
  readonly ROTATION_SPEED = Math.PI * 2.5; // rad/s
  readonly BULLET_SPEED = 500;
  readonly BULLET_COOLDOWN = 0.18; // seconds
  readonly TRAPPED_DURATION = 8.0; // seconds before bubble bursts & enemy escapes
  readonly INVULN_DURATION = 2.0; // seconds

  constructor(width = 800, height = 600) {
    this.width = width;
    this.height = height;

    this.player = {
      x: width / 2,
      y: height / 2,
      vx: 0,
      vy: 0,
      angle: -Math.PI / 2, // Facing up
      radius: 14,
      lives: 3,
      invulnTimer: 0,
      fireCooldown: 0,
    };
  }

  public startGame(): void {
    this.state = 'playing';
    this.score = 0;
    this.wave = 1;
    this.player.lives = 3;
    this.player.invulnTimer = 0;
    this.player.fireCooldown = 0;
    this.resetPlayerPosition();
    this.bullets = [];
    this.particles = [];
    this.popEvents = [];
    this.spawnWave(this.wave);
  }

  public restart(): void {
    this.startGame();
  }

  public resetPlayerPosition(): void {
    this.player.x = this.width / 2;
    this.player.y = this.height / 2;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.angle = -Math.PI / 2;
  }

  public spawnWave(wave: number): void {
    this.enemies = [];
    this.bullets = [];
    this.wave = wave;

    const count = 3 + wave * 2;
    const speedMult = 1 + (wave - 1) * 0.15;

    for (let i = 0; i < count; i++) {
      // Spawn away from center where player is
      let x = Math.random() * this.width;
      let y = Math.random() * this.height;
      while (Math.hypot(x - this.player.x, y - this.player.y) < 150) {
        x = Math.random() * this.width;
        y = Math.random() * this.height;
      }

      let type: EnemyType = 'scout';
      if (wave >= 2 && Math.random() < 0.4) type = 'chaser';
      if (wave >= 3 && Math.random() < 0.25) type = 'heavy';

      let baseSpeed = 80;
      let radius = 16;
      if (type === 'chaser') {
        baseSpeed = 110;
        radius = 12;
      } else if (type === 'heavy') {
        baseSpeed = 55;
        radius = 22;
      }

      const speed = baseSpeed * speedMult;
      const angle = Math.random() * Math.PI * 2;

      this.enemies.push({
        id: this.nextId++,
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius,
        type,
        state: 'normal',
        trappedTimer: 0,
        maxTrappedTime: this.TRAPPED_DURATION,
      });
    }
  }

  public update(dt: number, input: InputState): void {
    if (this.state === 'start') {
      if (input.firePressed || input.restartPressed) {
        this.startGame();
      }
      return;
    }

    if (this.state === 'gameover') {
      if (input.firePressed || input.restartPressed) {
        this.restart();
      }
      return;
    }

    // --- Playing State Updates ---
    this.popEvents = []; // reset per-frame pop events

    this.updatePlayer(dt, input);
    this.updateBullets(dt);
    this.updateEnemies(dt);
    this.updateParticles(dt);
    this.checkCollisions();

    // Check Wave Clear
    if (this.enemies.length === 0) {
      if (this.waveTransitionTimer <= 0) {
        this.waveTransitionTimer = 1.5; // 1.5 seconds delay before next wave
      } else {
        this.waveTransitionTimer -= dt;
        if (this.waveTransitionTimer <= 0) {
          this.spawnWave(this.wave + 1);
        }
      }
    }

    // High Score tracking
    if (this.score > this.highScore) {
      this.highScore = this.score;
    }
  }

  private updatePlayer(dt: number, input: InputState): void {
    const p = this.player;

    if (p.invulnTimer > 0) {
      p.invulnTimer = Math.max(0, p.invulnTimer - dt);
    }
    if (p.fireCooldown > 0) {
      p.fireCooldown = Math.max(0, p.fireCooldown - dt);
    }

    // Rotation
    if (input.left) {
      p.angle -= this.ROTATION_SPEED * dt;
    }
    if (input.right) {
      p.angle += this.ROTATION_SPEED * dt;
    }

    // Thrust / Movement (Up key or WASD W)
    if (input.up) {
      p.vx += Math.cos(p.angle) * this.PLAYER_ACCEL * dt;
      p.vy += Math.sin(p.angle) * this.PLAYER_ACCEL * dt;

      // Spawn thruster particles
      this.spawnThrustParticles();
    }

    // Damping / Friction
    p.vx *= Math.pow(this.PLAYER_FRICTION, dt * 60);
    p.vy *= Math.pow(this.PLAYER_FRICTION, dt * 60);

    // Max speed cap
    const speed = Math.hypot(p.vx, p.vy);
    if (speed > this.PLAYER_MAX_SPEED) {
      p.vx = (p.vx / speed) * this.PLAYER_MAX_SPEED;
      p.vy = (p.vy / speed) * this.PLAYER_MAX_SPEED;
    }

    // Apply Velocity
    p.x += p.vx * dt;
    p.y += p.vy * dt;

    // Screen Wrap
    p.x = (p.x + this.width) % this.width;
    p.y = (p.y + this.height) % this.height;

    // Shooting
    if ((input.fire || input.firePressed) && p.fireCooldown <= 0) {
      this.fireBullet();
      p.fireCooldown = this.BULLET_COOLDOWN;
    }
  }

  private fireBullet(): void {
    const p = this.player;
    const bulletVx = Math.cos(p.angle) * this.BULLET_SPEED + p.vx * 0.3;
    const bulletVy = Math.sin(p.angle) * this.BULLET_SPEED + p.vy * 0.3;

    // Muzzle offset
    const spawnX = p.x + Math.cos(p.angle) * (p.radius + 4);
    const spawnY = p.y + Math.sin(p.angle) * (p.radius + 4);

    this.bullets.push({
      id: this.nextId++,
      x: spawnX,
      y: spawnY,
      vx: bulletVx,
      vy: bulletVy,
      radius: 4,
      life: 1.2, // seconds
    });
  }

  private updateBullets(dt: number): void {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;

      // Screen Wrap
      b.x = (b.x + this.width) % this.width;
      b.y = (b.y + this.height) % this.height;

      if (b.life <= 0) {
        this.bullets.splice(i, 1);
      }
    }
  }

  private updateEnemies(dt: number): void {
    for (const e of this.enemies) {
      if (e.state === 'normal') {
        // Chaser steering
        if (e.type === 'chaser') {
          const dx = this.player.x - e.x;
          const dy = this.player.y - e.y;
          const angle = Math.atan2(dy, dx);
          const chaserAccel = 120 * dt;
          e.vx += Math.cos(angle) * chaserAccel;
          e.vy += Math.sin(angle) * chaserAccel;

          // Limit chaser speed
          const spd = Math.hypot(e.vx, e.vy);
          const maxSpd = 140 * (1 + (this.wave - 1) * 0.1);
          if (spd > maxSpd) {
            e.vx = (e.vx / spd) * maxSpd;
            e.vy = (e.vy / spd) * maxSpd;
          }
        }

        e.x += e.vx * dt;
        e.y += e.vy * dt;

        // Screen Wrap
        e.x = (e.x + this.width) % this.width;
        e.y = (e.y + this.height) % this.height;
      } else if (e.state === 'trapped') {
        // Float gently in current direction
        e.x += e.vx * dt * 0.2;
        e.y += e.vy * dt * 0.2;

        // Screen Wrap
        e.x = (e.x + this.width) % this.width;
        e.y = (e.y + this.height) % this.height;

        // Count down trapped time
        e.trappedTimer -= dt;
        if (e.trappedTimer <= 0) {
          // Escapes bubble as enraged!
          e.state = 'normal';
          const boostAngle = Math.random() * Math.PI * 2;
          const boostSpeed = 160;
          e.vx = Math.cos(boostAngle) * boostSpeed;
          e.vy = Math.sin(boostAngle) * boostSpeed;
          this.spawnBurstParticles(e.x, e.y, '#ff3366', 10);
        }
      }
    }
  }

  private checkCollisions(): void {
    // 1. Bullets vs Enemies
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      let bulletHit = false;

      for (const e of this.enemies) {
        const dist = Math.hypot(b.x - e.x, b.y - e.y);
        if (dist < b.radius + e.radius) {
          bulletHit = true;
          if (e.state === 'normal') {
            // Trap enemy in bubble!
            e.state = 'trapped';
            e.trappedTimer = e.maxTrappedTime;
            this.spawnBurstParticles(e.x, e.y, '#00ffff', 8);
          } else if (e.state === 'trapped') {
            // Shooting a trapped bubble pops it and triggers chain reaction!
            this.popBubble(e, 1);
          }
          break;
        }
      }

      if (bulletHit) {
        this.bullets.splice(bi, 1);
      }
    }

    // 2. Player vs Enemies (Normal & Trapped)
    const p = this.player;
    for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
      const e = this.enemies[ei];
      const dist = Math.hypot(p.x - e.x, p.y - e.y);

      if (dist < p.radius + e.radius) {
        if (e.state === 'trapped') {
          // Player touches trapped bubble: pop it and trigger chain reaction!
          this.popBubble(e, 1);
        } else if (e.state === 'normal') {
          // Player hit by active enemy!
          if (p.invulnTimer <= 0) {
            this.hitPlayer();
          }
        }
      }
    }
  }

  public popBubble(targetEnemy: Enemy, comboLevel: number = 1): void {
    const idx = this.enemies.indexOf(targetEnemy);
    if (idx === -1) return;

    // Remove popped enemy
    this.enemies.splice(idx, 1);

    // Score calculation with multiplier
    const basePoints = targetEnemy.type === 'heavy' ? 250 : targetEnemy.type === 'chaser' ? 150 : 100;
    const points = basePoints * Math.pow(2, comboLevel - 1);
    this.score += points;

    this.popEvents.push({
      x: targetEnemy.x,
      y: targetEnemy.y,
      comboCount: comboLevel,
      scoreGained: points,
    });

    // Particle effect
    const popColor = comboLevel > 1 ? '#ff00ff' : '#00ffff';
    this.spawnBurstParticles(targetEnemy.x, targetEnemy.y, popColor, 16 + comboLevel * 4);

    // Shockwave pop radius for chain reaction
    const SHOCKWAVE_RADIUS = 90 + comboLevel * 10;

    // Trigger chain pop for adjacent trapped bubbles
    // Collect trapped enemies within shockwave
    const chainTargets: Enemy[] = [];
    for (const other of this.enemies) {
      if (other.state === 'trapped') {
        const dist = Math.hypot(targetEnemy.x - other.x, targetEnemy.y - other.y);
        if (dist <= SHOCKWAVE_RADIUS) {
          chainTargets.push(other);
        }
      }
    }

    // Recursively pop chain targets with incremented combo level
    for (const chainTarget of chainTargets) {
      this.popBubble(chainTarget, comboLevel + 1);
    }
  }

  private hitPlayer(): void {
    const p = this.player;
    p.lives -= 1;
    p.invulnTimer = this.INVULN_DURATION;

    this.spawnBurstParticles(p.x, p.y, '#ff0055', 25);

    if (p.lives <= 0) {
      this.state = 'gameover';
    } else {
      this.resetPlayerPosition();
    }
  }

  private spawnThrustParticles(): void {
    const p = this.player;
    const backAngle = p.angle + Math.PI;
    const offsetX = Math.cos(backAngle) * (p.radius + 2);
    const offsetY = Math.sin(backAngle) * (p.radius + 2);

    const spread = (Math.random() - 0.5) * 0.5;
    const pSpeed = 100 + Math.random() * 80;

    this.particles.push({
      x: p.x + offsetX,
      y: p.y + offsetY,
      vx: Math.cos(backAngle + spread) * pSpeed + p.vx * 0.2,
      vy: Math.sin(backAngle + spread) * pSpeed + p.vy * 0.2,
      color: Math.random() < 0.5 ? '#00ffff' : '#ff00ff',
      life: 0.25,
      maxLife: 0.25,
      size: 3 + Math.random() * 2,
    });
  }

  private spawnBurstParticles(x: number, y: number, color: string, count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 200;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        life: 0.4 + Math.random() * 0.3,
        maxLife: 0.7,
        size: 2 + Math.random() * 3,
      });
    }
  }

  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const pt = this.particles[i];
      pt.x += pt.vx * dt;
      pt.y += pt.vy * dt;
      pt.life -= dt;
      if (pt.life <= 0) {
        this.particles.splice(i, 1);
      }
    }
  }
}
